/**
 * InBodyCliente.jsx
 * Vista do relatório InBody para a app da cliente.
 * Linguagem simples, visual, motivadora — sem jargão técnico.
 *
 * Props:
 *   data — objecto devolvido pelo parser InBody
 *   firstName — string (ex: "Cristina")
 */

import { useState } from "react";
import { InBodyTooltip, CLIENT_TOOLTIPS } from "./InBodyTooltips";

// ---------------------------------------------------------------------------
// Dados de demonstração
// ---------------------------------------------------------------------------
const DEMO = {
  firstName: "Cristina",
  test_datetime: "2024-11-29T00:00:00",
  weight_kg: 60.8,
  bfm_kg: 22.3,
  smm_kg: 20.9,
  ffm_kg: 38.5,
  tbw_l: 28.2,
  pbf: 36.6,
  bmi: 23.8,
  inbody_score: 67,
  vfl: 11,
  whr: 0.92,
  bmr_kcal: 1203,
  recommended_calories: 1385,
  abdomen_circumference_cm: 86,
  control_targets: {
    target_weight_kg: 55.0,
    weight_control_kg: -5.8,
    bfm_control_kg: -9.6,
    ffm_control_kg: 3.8,
  },
  segments: {
    lean_mass: {
      right_arm: { kg: 1.91, pct: 92.1 },
      left_arm:  { kg: 1.82, pct: 87.8 },
      trunk:     { kg: 17.7, pct: 94.1 },
      right_leg: { kg: 5.83, pct: 88.9 },
      left_leg:  { kg: 5.87, pct: 89.5 },
    },
    fat_mass: {
      right_arm: { kg: 1.6,  pct: 169.5 },
      left_arm:  { kg: 1.6,  pct: 174.5 },
      trunk:     { kg: 11.3, pct: 218.6 },
      right_leg: { kg: 3.3,  pct: 141.6 },
      left_leg:  { kg: 3.3,  pct: 141.6 },
    },
  },
  reference_ranges: {
    bfm: { min: 11.0, max: 17.6 },
    pbf: { min: 18.0, max: 28.0 },
    bmi: { min: 18.5, max: 25.0 },
    vfl: { min: 1,    max: 9    },
  },
};

// ---------------------------------------------------------------------------
// Paleta (verde-sálvia, quente, feminina)
// ---------------------------------------------------------------------------
const C = {
  bg:        "#F8F6F2",
  card:      "#FFFFFF",
  sage:      "#4E7052",
  sageLight: "#EAF1EA",
  sageMid:   "#8CB390",
  amber:     "#C4763A",
  amberLight:"#FAF0E4",
  red:       "#B84444",
  blue:      "#4A7FAB",
  blueLight: "#EAF2FA",
  text:      "#1C2420",
  muted:     "#728070",
  border:    "#E0DDD7",
  white:     "#FFFFFF",
};

const fmt = (n, d = 1) => n != null ? Number(n).toFixed(d) : "-";

// ---------------------------------------------------------------------------
// Score visual em arco
// ---------------------------------------------------------------------------
function ScoreArc({ score }) {
  const radius = 54;
  const stroke = 10;
  const cx = 70, cy = 70;
  const circumference = Math.PI * radius; // semicírculo
  const progress = (score / 100) * circumference;
  const color = score >= 70 ? C.sage : score >= 50 ? C.amber : C.red;

  return (
    <svg width={140} height={85} viewBox="0 0 140 85">
      <path
        d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
        fill="none" stroke={C.border} strokeWidth={stroke} strokeLinecap="round"
      />
      <path
        d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
        fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${progress} ${circumference}`}
        style={{ transition: "stroke-dasharray 1s ease" }}
      />
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize={28} fontWeight={700} fill={color}>{score}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize={11} fill={C.muted}>/ 100</text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Barra de progresso simples
// ---------------------------------------------------------------------------
function MiniBar({ value, max, color, height = 8 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div style={{ height, borderRadius: height / 2, background: C.border, overflow: "hidden" }}>
      <div style={{
        height: "100%", borderRadius: height / 2,
        width: `${pct}%`, background: color,
        transition: "width 0.8s ease",
      }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card de métrica com ícone SVG inline
// ---------------------------------------------------------------------------
function StatCard({ icon, label, value, unit, sub, color = C.sage, bg, tooltipKey }) {
  const tip = tooltipKey ? CLIENT_TOOLTIPS[tooltipKey] : null;
  return (
    <div style={{
      background: bg || C.card, border: `1px solid ${C.border}`,
      borderRadius: 16, padding: "16px", display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ fontSize: 20 }}>{icon}</div>
      <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, display: "flex", alignItems: "center" }}>
        {label}
        {tip && <InBodyTooltip short={tip.short} expanded={tip.expanded} />}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: "-0.03em" }}>{value}</span>
        {unit && <span style={{ fontSize: 13, color: C.muted }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Silhueta corporal com distribuição de gordura
// ---------------------------------------------------------------------------
function BodySilhouette({ segments }) {
  const fat = segments?.fat_mass || {};
  const lean = segments?.lean_mass || {};

  // Intensidade de gordura: pct > 150 = muito, 100-150 = algum, < 100 = pouco
  const fatColor = (pct) => {
    if (!pct) return C.sageLight;
    if (pct > 150) return "#E8A070";
    if (pct > 100) return "#F0C498";
    return C.sageLight;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <svg width={160} height={260} viewBox="0 0 160 260">
        {/* Cabeça */}
        <circle cx={80} cy={24} r={18} fill={C.border} stroke={C.border} strokeWidth={1} />

        {/* Pescoço */}
        <rect x={72} y={40} width={16} height={14} rx={4} fill={C.border} />

        {/* Tronco */}
        <rect x={44} y={54} width={72} height={80} rx={14}
          fill={fatColor(fat.trunk?.pct)}
          stroke={C.sage} strokeWidth={1.5} />
        <text x={80} y={97} textAnchor="middle" fontSize={10} fill={C.text} fontWeight={600}>
          {fmt(fat.trunk?.kg)} kg
        </text>
        <text x={80} y={110} textAnchor="middle" fontSize={9} fill={C.muted}>gordura</text>

        {/* Braço direito */}
        <rect x={12} y={58} width={28} height={64} rx={10}
          fill={fatColor(fat.right_arm?.pct)}
          stroke={C.sage} strokeWidth={1} />
        <text x={26} y={93} textAnchor="middle" fontSize={9} fill={C.text} fontWeight={600}>
          {fmt(fat.right_arm?.kg)}
        </text>
        <text x={26} y={104} textAnchor="middle" fontSize={8} fill={C.muted}>kg</text>

        {/* Braço esquerdo */}
        <rect x={120} y={58} width={28} height={64} rx={10}
          fill={fatColor(fat.left_arm?.pct)}
          stroke={C.sage} strokeWidth={1} />
        <text x={134} y={93} textAnchor="middle" fontSize={9} fill={C.text} fontWeight={600}>
          {fmt(fat.left_arm?.kg)}
        </text>
        <text x={134} y={104} textAnchor="middle" fontSize={8} fill={C.muted}>kg</text>

        {/* Anca */}
        <rect x={42} y={134} width={76} height={24} rx={10} fill={C.border} />

        {/* Perna direita */}
        <rect x={42} y={158} width={34} height={86} rx={12}
          fill={fatColor(fat.right_leg?.pct)}
          stroke={C.sage} strokeWidth={1} />
        <text x={59} y={204} textAnchor="middle" fontSize={9} fill={C.text} fontWeight={600}>
          {fmt(fat.right_leg?.kg)}
        </text>
        <text x={59} y={215} textAnchor="middle" fontSize={8} fill={C.muted}>kg</text>

        {/* Perna esquerda */}
        <rect x={84} y={158} width={34} height={86} rx={12}
          fill={fatColor(fat.left_leg?.pct)}
          stroke={C.sage} strokeWidth={1} />
        <text x={101} y={204} textAnchor="middle" fontSize={9} fill={C.text} fontWeight={600}>
          {fmt(fat.left_leg?.kg)}
        </text>
        <text x={101} y={215} textAnchor="middle" fontSize={8} fill={C.muted}>kg</text>
      </svg>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.muted }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: "#E8A070" }} />
          mais gordura
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: C.sageLight }} />
          dentro do normal
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export default function InBodyCliente({ data = DEMO, firstName, allRecords = [], currentDate = '' }) {
  const [page, setPage] = useState(0);
  const d = data;
  const name = firstName || d.firstName || "Cristina";
  const date = d.test_datetime
    ? new Date(d.test_datetime).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })
    : "-";

  const pages = ["Resumo", "Corpo", "Objetivos"];

  const totalFat  = d.bfm_kg;
  const totalMusc = d.smm_kg;
  const total     = d.weight_kg;

  // Previous record for variance display (Item 8 — parity with nutri view)
  const sortedRecords = [...allRecords].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const currentIdx = sortedRecords.findIndex(r => r.date === currentDate);
  const prevRecord = currentIdx > 0 ? sortedRecords[currentIdx - 1]?.data : null;
  const getLeanDelta = (key) => {
    if (!prevRecord) return null;
    const curr = d.segments?.lean_mass?.[key]?.kg;
    const prev = prevRecord.segments?.lean_mass?.[key]?.kg;
    if (curr == null || prev == null) return null;
    const diff = curr - prev;
    if (Math.abs(diff) < 0.05) return { text: '=', color: C.muted };
    return { text: `${diff > 0 ? '+' : ''}${diff.toFixed(1)} kg`, color: diff > 0 ? C.sage : C.red };
  };

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      background: C.bg, minHeight: "100vh",
      color: C.text, maxWidth: 420, margin: "0 auto",
    }}>
      {/* Header */}
      <div style={{
        background: C.sage, padding: "24px 20px 20px", color: C.white,
      }}>
        <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4, letterSpacing: "0.05em" }}>
          RELATÓRIO INBODY · {date}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Olá, {name}!</div>
        <div style={{ fontSize: 14, opacity: 0.85, marginTop: 4, lineHeight: 1.5 }}>
          Aqui tens os resultados da tua avaliação de composição corporal.
        </div>
      </div>

      {/* Nav */}
      <div style={{
        display: "flex", background: C.white, borderBottom: `1px solid ${C.border}`,
        position: "sticky", top: 0, zIndex: 10,
      }}>
        {pages.map((p, i) => (
          <button key={p} onClick={() => setPage(i)} style={{
            flex: 1, padding: "12px 0", border: "none", background: "transparent",
            cursor: "pointer", fontSize: 13, fontWeight: 500,
            color: page === i ? C.sage : C.muted,
            borderBottom: page === i ? `2px solid ${C.sage}` : "2px solid transparent",
            transition: "all 0.15s",
          }}>
            {p}
          </button>
        ))}
      </div>

      <div style={{ padding: "20px 16px", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* ---- RESUMO ---- */}
        {page === 0 && (
          <>
            {/* Score */}
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 16, padding: "20px", display: "flex",
              flexDirection: "column", alignItems: "center", gap: 4,
            }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.muted, alignSelf: "flex-start", display: "flex", alignItems: "center" }}>A tua pontuação InBody<InBodyTooltip short={CLIENT_TOOLTIPS.inbody_score.short} expanded={CLIENT_TOOLTIPS.inbody_score.expanded} /></div>
              <ScoreArc score={d.inbody_score || 0} />
              <div style={{ fontSize: 13, color: C.muted, textAlign: "center", maxWidth: 220 }}>
                {d.inbody_score >= 80
                  ? "Excelente composição corporal!"
                  : d.inbody_score >= 60
                  ? "Boa base para trabalhar. Há espaço para melhorar!"
                  : "Vamos trabalhar juntas para melhorar este valor."}
              </div>
            </div>

            {/* Peso e composição */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.muted, marginBottom: 12 }}>O que é o teu peso</div>
              <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.03em", marginBottom: 12 }}>
                {fmt(total)} <span style={{ fontSize: 16, fontWeight: 400, color: C.muted }}>kg</span>
              </div>

              {[
                { label: "Massa gorda", value: totalFat, pct: (totalFat / total * 100).toFixed(1), color: C.amber, tipKey: "massa_gorda" },
                { label: "Massa muscular", value: totalMusc, pct: (totalMusc / total * 100).toFixed(1), color: C.sage, tipKey: "massa_muscular" },
                { label: "Outros (água, osso…)", value: total - totalFat - totalMusc, pct: ((total - totalFat - totalMusc) / total * 100).toFixed(1), color: C.border },
              ].map((s) => (
                <div key={s.label} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4, alignItems: "center" }}>
                    <span style={{ color: C.text, display: "flex", alignItems: "center" }}>
                      {s.label}
                      {s.tipKey && <InBodyTooltip short={CLIENT_TOOLTIPS[s.tipKey]?.short} expanded={CLIENT_TOOLTIPS[s.tipKey]?.expanded} />}
                    </span>
                    <span style={{ fontWeight: 600 }}>{fmt(s.value)} kg <span style={{ color: C.muted, fontWeight: 400 }}>({s.pct}%)</span></span>
                  </div>
                  <MiniBar value={s.value} max={total} color={s.color} />
                </div>
              ))}
            </div>

            {/* Cards rápidos */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <StatCard icon="💧" label="Água no corpo" value={fmt(d.tbw_l)} unit="L"
                sub="Hidratação geral" tooltipKey="agua" />
              <StatCard icon="📏" label="IMC" value={fmt(d.bmi)} unit=""
                color={d.bmi >= 18.5 && d.bmi <= 25 ? C.sage : C.amber}
                sub={d.bmi >= 18.5 && d.bmi <= 25 ? "Intervalo normal" : "Fora do intervalo"} tooltipKey="imc" />
              <StatCard icon="🫀" label="Gordura visceral" value={d.vfl} unit="nível"
                color={d.vfl <= 9 ? C.sage : C.red}
                sub={d.vfl <= 9 ? "Bom" : "Acima do recomendado"} tooltipKey="gordura_visceral" />
              <StatCard icon="📐" label="Cintura" value={fmt(d.abdomen_circumference_cm, 0)} unit="cm"
                sub="Perímetro abdominal" tooltipKey="circunferencia_abdominal" />
            </div>
          </>
        )}

        {/* ---- CORPO ---- */}
        {page === 1 && (
          <>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.muted, marginBottom: 4 }}>Onde está a gordura?</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>
                A cor mais laranja indica maior concentração de gordura. O tronco é a zona com mais acumulação.
              </div>
              <BodySilhouette segments={d.segments} />
            </div>

            {/* Tabela de segmentos */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.muted, marginBottom: 4 }}>Músculo por zona</div>
              {prevRecord && <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Variação face à avaliação anterior ({sortedRecords[currentIdx - 1]?.date || ''}).</div>}
              {[
                { label: "Braço direito", key: "right_arm" },
                { label: "Braço esquerdo", key: "left_arm" },
                { label: "Tronco", key: "trunk" },
                { label: "Perna direita", key: "right_leg" },
                { label: "Perna esquerda", key: "left_leg" },
              ].map(({ label, key }) => {
                const seg = d.segments?.lean_mass?.[key];
                const ok = seg?.pct >= 80 && seg?.pct <= 120;
                const delta = getLeanDelta(key);
                return (
                  <div key={key} style={{ marginBottom: 10 }} data-testid={`cliente-seg-${key}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
                      <span>{label}</span>
                      <span style={{ fontWeight: 600, color: ok ? C.sage : C.amber, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {fmt(seg?.kg)} kg
                        {delta && <span style={{ fontSize: 10, fontWeight: 600, color: delta.color }} data-testid={`cliente-seg-delta-${key}`}>{delta.text}</span>}
                      </span>
                    </div>
                    <MiniBar value={seg?.pct || 0} max={130} color={ok ? C.sage : C.amber} height={6} />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ---- OBJETIVOS ---- */}
        {page === 2 && (
          <>
            <div style={{
              background: C.sageLight, border: `1px solid ${C.sageMid}`,
              borderRadius: 16, padding: 16,
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>O teu objetivo de composição</div>
              <div style={{ fontSize: 13, color: C.sage, lineHeight: 1.6 }}>
                Com base na tua avaliação, o InBody sugere chegar a <strong>{fmt(d.control_targets?.target_weight_kg)} kg</strong>, reduzindo gordura e ganhando músculo.
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { icon: "⚖️", label: "Peso alvo", value: `${fmt(d.control_targets?.target_weight_kg)} kg`, neutral: true },
                { icon: "📉", label: "Ajuste total", value: `${fmt(d.control_targets?.weight_control_kg)} kg`, color: C.red },
                { icon: "🥊", label: "Perder gordura", value: `${fmt(d.control_targets?.bfm_control_kg)} kg`, color: C.amber },
                { icon: "💪", label: "Ganhar músculo", value: `+${fmt(d.control_targets?.ffm_control_kg)} kg`, color: C.sage },
              ].map((item) => (
                <div key={item.label} style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 16, padding: "14px 16px",
                }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{item.icon}</div>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginBottom: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: item.color || C.text, letterSpacing: "-0.02em" }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              fontSize: 12, color: C.muted, textAlign: "center",
              padding: "8px 16px", lineHeight: 1.6,
            }}>
              Estes valores são sugestões do InBody. A tua nutricionista irá personalizar o plano com base em todos os teus dados.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
